// src/utils/letterRedaction.ts
// ──────────────────────────────────────────────────────────────
// Redacts PHI from assembled letters before database storage
// and hydrates them back on load from local case data.
//
// Uses TWO redaction strategies:
//   1. PATTERN-BASED: Matches structural positions in the letter
//      where PHI appears (e.g., "Patient: ___") and replaces
//      whatever is there — catches manually-typed real names.
//   2. VALUE-BASED: Matches known PHI values anywhere in the text
//      as a safety net (e.g., the friendly name in body text).
//
// Physician credentials (name, NPI, practice info) are NOT
// considered PHI and are stored as-is since they're professional
// identifiers, not patient data.
// ──────────────────────────────────────────────────────────────

// Placeholder tokens used in stored letter text
const REDACTION_TOKENS = {
    patient_name: "[PATIENT_NAME]",
    patient_dob: "[DOB]",
    member_id: "[MEMBER_ID]",
  } as const;
  
  // Structural patterns where PHI appears in letter templates.
  // These match regardless of what value is in the field.
  const PHI_PATTERNS: { pattern: RegExp; replacement: string }[] = [
    // "Patient: <anything>" line
    {
      pattern: /^(Patient:\s*)(.+)$/gm,
      replacement: `$1${REDACTION_TOKENS.patient_name}`,
    },
    // "Date of Birth: <anything>" line
    {
      pattern: /^(Date of Birth:\s*)(.+)$/gm,
      replacement: `$1${REDACTION_TOKENS.patient_dob}`,
    },
    // "Member ID: <anything>" line
    {
      pattern: /^(Member ID:\s*)(.+)$/gm,
      replacement: `$1${REDACTION_TOKENS.member_id}`,
    },
  ];
  
  interface RedactionContext {
    /** Patient's friendly name (or real name if entered) */
    patient_name?: string;
    /** Date of birth if collected */
    patient_dob?: string;
    /** Insurance member ID if collected */
    member_id?: string;
  }
  
  /**
   * Redact PHI from an assembled letter before storing in the database.
   *
   * Strategy:
   *   1. Pattern-based: Replace content in structural lines (Patient:, DOB:, Member ID:)
   *      regardless of what value is there. This catches manually-typed real names.
   *   2. Value-based: Replace any remaining occurrences of known PHI values
   *      anywhere in the text (e.g., if the name appears in body paragraphs).
   *   3. Normalize bracket-style placeholders to standard tokens.
   */
  export function redactLetterPHI(
    letterText: string,
    context: RedactionContext
  ): string {
    let redacted = letterText;
  
    // ── 1. Pattern-based redaction (structural lines) ──────────
    for (const { pattern, replacement } of PHI_PATTERNS) {
      pattern.lastIndex = 0;
      redacted = redacted.replace(pattern, replacement);
    }
  
    // ── 2. Value-based redaction (known values anywhere) ───────
    // Catches the patient name if it appears in the body text,
    // not just in the "Patient:" line
    if (context.patient_name?.trim()) {
      const name = context.patient_name.trim();
      if (name !== REDACTION_TOKENS.patient_name) {
        redacted = redacted.split(name).join(REDACTION_TOKENS.patient_name);
      }
    }
  
    if (context.patient_dob?.trim()) {
      const dob = context.patient_dob.trim();
      if (dob !== REDACTION_TOKENS.patient_dob) {
        redacted = redacted.split(dob).join(REDACTION_TOKENS.patient_dob);
      }
    }
  
    if (context.member_id?.trim()) {
      const memberId = context.member_id.trim();
      if (memberId !== REDACTION_TOKENS.member_id) {
        redacted = redacted.split(memberId).join(REDACTION_TOKENS.member_id);
      }
    }
  
    // ── 3. Normalize bracket-style placeholders ────────────────
    redacted = redacted.replace(/\[Patient Name\]/g, REDACTION_TOKENS.patient_name);
    redacted = redacted.replace(/\[Patient\]/g, REDACTION_TOKENS.patient_name);
    redacted = redacted.replace(/\[DOB\]/g, REDACTION_TOKENS.patient_dob);
    redacted = redacted.replace(/\[Member ID\]/g, REDACTION_TOKENS.member_id);
  
    return redacted;
  }
  
  /**
   * Hydrate a stored (redacted) letter with PHI from local case data.
   *
   * Replaces placeholder tokens with actual values. If a value isn't
   * available, falls back to a user-friendly bracket placeholder.
   */
  export function hydrateLetterPHI(
    storedText: string,
    context: RedactionContext
  ): string {
    let hydrated = storedText;
  
    hydrated = hydrated
      .split(REDACTION_TOKENS.patient_name)
      .join(context.patient_name?.trim() || "[Patient Name]");
  
    hydrated = hydrated
      .split(REDACTION_TOKENS.patient_dob)
      .join(context.patient_dob?.trim() || "[DOB]");
  
    hydrated = hydrated
      .split(REDACTION_TOKENS.member_id)
      .join(context.member_id?.trim() || "[Member ID]");
  
    return hydrated;
  }
  
  /**
   * Check whether a letter text contains any un-redacted PHI values.
   * Useful as a safety check before storing.
   */
  export function hasUnredactedPHI(
    letterText: string,
    context: RedactionContext
  ): boolean {
    if (context.patient_name?.trim()) {
      if (letterText.includes(context.patient_name.trim())) return true;
    }
    if (context.patient_dob?.trim()) {
      if (letterText.includes(context.patient_dob.trim())) return true;
    }
    if (context.member_id?.trim()) {
      if (letterText.includes(context.member_id.trim())) return true;
    }
  
    // Also check if structural lines still contain non-token values
    for (const { pattern } of PHI_PATTERNS) {
      pattern.lastIndex = 0;
      const matches = letterText.match(pattern);
      if (matches) {
        for (const match of matches) {
          const hasToken = Object.values(REDACTION_TOKENS).some(t => match.includes(t));
          if (!hasToken) return true;
        }
      }
    }
  
    return false;
  }